import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/material';
import {
  Add,
  FilterList,
  MoreVert,
  Search,
  FileDownload,
  Edit,
  Delete
} from '@mui/icons-material';

// Meeting Dialog Component
const MeetingDialog = ({ open, onClose, meeting, onSave }) => {
  const [formData, setFormData] = useState(
    meeting || {
      title: '',
      host: '',
      startOn: '',
      endOn: '',
      status: 'Waiting'
    }
  );

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = () => {
    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{meeting ? 'Edit Meeting' : 'Add New Meeting'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            name="title"
            label="Meeting Title"
            fullWidth
            value={formData.title}
            onChange={handleChange}
          />
          <TextField
            name="host"
            label="Meeting Host"
            fullWidth
            value={formData.host}
            onChange={handleChange}
          />
          <TextField
            name="startOn"
            label="Start Time"
            type="datetime-local"
            fullWidth
            value={formData.startOn}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            name="endOn"
            label="End Time"
            type="datetime-local"
            fullWidth
            value={formData.endOn}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              label="Status"
              onChange={handleChange}
            >
              <MenuItem value="Waiting">Waiting</MenuItem>
              <MenuItem value="Live">Live</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" sx={{ background: '#1d82f6' }}>
          {meeting ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Zoom = () => {
  const [duration, setDuration] = useState('week');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('startOn');
  const [order, setOrder] = useState('asc');
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMeeting, setEditMeeting] = useState(null);

  const [meetings, setMeetings] = useState([
    {
      id: 1,
      title: 'Interview',
      host: 'Prof. Amya Zemlak',
      startOn: '2025-01-24T08:15',
      endOn: '2025-01-24T13:45',
      status: 'Waiting'
    },
    {
      id: 2,
      title: 'Interview',
      host: 'Prof. Amya Zemlak',
      startOn: '2025-01-24T08:15',
      endOn: '2025-01-24T13:45',
      status: 'Waiting'
    }
  ]);

  const handleDelete = (meetingId) => {
    setMeetings(meetings.filter(meeting => meeting.id !== meetingId));
    setSelected(selected.filter(id => id !== meetingId));
    handleActionClose();
  };

  const handleOpenDialog = (meeting = null) => {
    setEditMeeting(meeting);
    setDialogOpen(true);
    handleActionClose();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMeeting(null);
  };

  const handleSaveMeeting = (meetingData) => {
    if (editMeeting) {
      setMeetings(meetings.map(meeting =>
        meeting.id === editMeeting.id
          ? { ...meetingData, id: meeting.id }
          : meeting
      ));
    } else {
      const newMeeting = {
        ...meetingData,
        id: Math.max(...meetings.map(m => m.id), 0) + 1
      };
      setMeetings([...meetings, newMeeting]);
    }
  };

  const handleActionClick = (event, row) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedRow(null);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(meetings.map(meeting => meeting.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter(item => item !== id);
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredMeetings = meetings.filter(meeting =>
    Object.values(meeting).some(value =>
      value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const paginatedMeetings = filteredMeetings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const checkboxStyle = {
    color: 'gray',
    '&.Mui-checked': {
      color: '#1d82f6',
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Top Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel sx={{ color: 'black', backgroundColor: 'white', px: 0.5 }}>Duration</InputLabel>
          <Select
            value={duration}
            label="Duration"
            onChange={(e) => setDuration(e.target.value)}
            sx={{
              border: "1px solid gray",
              borderRadius: "5px",
              color: "black",
              '& .MuiSelect-select': {
                color: 'black'
              }
            }}
          >
            <MenuItem value="week">Past Week</MenuItem>
            <MenuItem value="month">1 Month</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ color: 'black', backgroundColor: 'white', px: 0.5 }}>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value)}
            sx={{
              border: "1px solid gray",
              borderRadius: "5px",
              color: "black",
              '& .MuiSelect-select': {
                color: 'black'
              }
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="waiting">Waiting</MenuItem>
            <MenuItem value="live">Live</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Start typing to search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: <Search sx={{ color: 'black', mr: 1 }} />,
            sx: { color: 'black', '&::placeholder': { color: 'black' } }
          }}
          sx={{
            flexGrow: 1,
            border: "1px solid gray",
            borderRadius: "5px",
            color: "black",
            '& .MuiInputBase-input': {
              color: 'black'
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'black',
              opacity: 0.7
            }
          }}
        />

        <Button
          variant="outlined"
          startIcon={<FilterList />}
          sx={{ whiteSpace: 'nowrap', color: 'gray', borderColor: 'gray' }}
        >
          Filters
        </Button>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ background: '#1d82f6' }}
            onClick={() => handleOpenDialog()}
          >
            Add Meeting
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            sx={{ color: 'gray', borderColor: 'gray' }}
          >
            Export
          </Button>
        </Box>

        {selected.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Select
              size="small"
              defaultValue=""
              sx={{
                border: "1px solid gray",
                borderRadius: "5px",
                color: "black"
              }}
            >
              <MenuItem value="delete" onClick={() => {
                selected.forEach(id => handleDelete(id));
                setSelected([]);
              }}>Delete Selected</MenuItem>
            </Select>
            <Button variant="contained" size="small" sx={{ background: "#1d82f6" }}>
              Apply
            </Button>
          </Box>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ bgcolor: 'white' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selected.length === filteredMeetings.length}
                  indeterminate={selected.length > 0 && selected.length < filteredMeetings.length}
                  onChange={handleSelectAll}
                  sx={checkboxStyle}
                />
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'id' ? order : false}
                onClick={() => handleSort('id')}
                sx={{ color: 'black' }}
              >
                Meeting Id
              </TableCell>
              <TableCell onClick={() => handleSort('title')} sx={{ color: 'black' }}>Meeting Title</TableCell>
              <TableCell onClick={() => handleSort('host')} sx={{ color: 'black' }}>Meeting Host</TableCell>
              <TableCell onClick={() => handleSort('startOn')} sx={{ color: 'black' }}>Start On</TableCell>
              <TableCell onClick={() => handleSort('endOn')} sx={{ color: 'black' }}>End On</TableCell>
              <TableCell onClick={() => handleSort('status')} sx={{ color: 'black' }}>Status</TableCell>
              <TableCell sx={{ color: 'black' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMeetings.map((meeting) => (
              <TableRow key={meeting.id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.includes(meeting.id)}
                    onChange={() => handleSelect(meeting.id)}
                    sx={checkboxStyle}
                  />
                </TableCell>
                <TableCell sx={{ color: 'black' }}>{meeting.id}</TableCell>
                <TableCell sx={{ color: 'black' }}>{meeting.title}</TableCell>
                <TableCell sx={{ color: 'black' }}>{meeting.host}</TableCell>
                <TableCell sx={{ color: 'black' }}>{meeting.startOn}</TableCell>
                <TableCell sx={{ color: 'black' }}>{meeting.endOn}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'black' }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: meeting.status === 'Waiting' ? 'warning.main' : 'success.main'
                      }}
                    />
                    {meeting.status}
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    sx={{ color: 'black' }}
                    onClick={(e) => handleActionClick(e, meeting)}
                  >
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => handleOpenDialog(selectedRow)}>
          <Edit sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedRow.id)}>
          <Delete sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Meeting Dialog */}
      <MeetingDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        meeting={editMeeting}
        onSave={handleSaveMeeting}
      />

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, color: 'black' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: 'black' }}>Show</Typography>
          <Select
            size="small"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            sx={{
              border: "1px solid gray",
              borderRadius: "5px",
              color: "black",
              '& .MuiSelect-select': {
                color: 'black'
              }
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
          <Typography variant="body2" sx={{ color: 'black' }}>entries</Typography>
        </Box>

        <TablePagination
          component="div"
          count={filteredMeetings.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[]}
          sx={{
            color: 'black',
            '& .MuiTablePagination-selectLabel': {
              color: 'black'
            },
            '& .MuiTablePagination-displayedRows': {
              color: 'black'
            },
            '& .MuiTablePagination-actions': {
              color: 'black'
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default Zoom;